import { useForm, usePage, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableTableHead } from "@/components/ui/sortable-table-head"
import { FlashMessages } from "@/components/FlashMessages"
import { FormError } from "@/components/FormError"
import {
  Plus,
  Pencil,
  Trash2,
  Cylinder,
} from "lucide-react"
import { useSortableData } from "@/hooks/useSortableData"
import { noAutofillProps } from "@/lib/form"
import type { AppPageProps } from "@/types"

interface ContainerType {
  id: string
  name: string
  created_at: string
}

interface ContainersPageProps extends AppPageProps {
  containers: ContainerType[]
}

type ContainerSortKey = "name" | "created_at"

const containerComparators: Record<ContainerSortKey, (a: ContainerType, b: ContainerType) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  created_at: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
}

export default function Containers() {
  const { t } = useTranslation()
  const { auth, flash, containers } = usePage<ContainersPageProps>().props
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingContainer, setEditingContainer] = useState<ContainerType | null>(null)

  const {
    sortedData: sortedContainers,
    sortKey: containerSortKey,
    sortDirection: containerSortDirection,
    handleSort: handleContainerSort,
  } = useSortableData(containers, "name", containerComparators)

  const createForm = useForm({
    name: "",
  })

  const editForm = useForm({
    name: "",
  })

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    createForm.post("/containers", {
      preserveState: true,
      onSuccess: () => {
        createForm.reset()
        setIsCreateDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingContainer) return

    editForm.patch(`/containers/${editingContainer.id}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingContainer(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(container: ContainerType) {
    setEditingContainer(container)
    editForm.setData({
      name: container.name,
    })
  }

  function handleDeleteContainer(container: ContainerType) {
    if (confirm(t("containers.confirmDelete"))) {
      router.delete(`/containers/${container.id}`)
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("containers.title")}</h2>
            <p className="text-muted-foreground">{t("containers.subtitle")}</p>
          </div>

          {/* Create Dialog */}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t("containers.addButton")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleCreateSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("containers.addButton")}</DialogTitle>
                  <DialogDescription>
                    {t("containers.addDescription")}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-name">{t("common.name")}</Label>
                    <Input
                      id="create-name"
                      value={createForm.data.name}
                      onChange={(e) => createForm.setData("name", e.target.value)}
                      placeholder={t("containers.namePlaceholder")}
                      required
                      {...noAutofillProps}
                    />
                    <FormError error={createForm.errors.name} />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={createForm.processing}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("common.add")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={!!editingContainer} onOpenChange={(open) => !open && setEditingContainer(null)}>
            <DialogContent>
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("containers.editContainer")}</DialogTitle>
                  <DialogDescription>{t("containers.editDescription")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">{t("common.name")}</Label>
                    <Input
                      id="edit-name"
                      value={editForm.data.name}
                      onChange={(e) => editForm.setData("name", e.target.value)}
                      placeholder={t("containers.namePlaceholder")}
                      required
                      {...noAutofillProps}
                    />
                    <FormError error={editForm.errors.name} />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingContainer(null)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Containers Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cylinder className="w-5 h-5" />
              {t("containers.section")}
            </CardTitle>
            <CardDescription>{t("containers.sectionDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {containers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("containers.noContainers")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      label={t("common.name")}
                      sortKey="name"
                      currentSortKey={containerSortKey}
                      direction={containerSortDirection}
                      onSort={handleContainerSort}
                    />
                    <SortableTableHead
                      label={t("common.createdAt")}
                      sortKey="created_at"
                      currentSortKey={containerSortKey}
                      direction={containerSortDirection}
                      onSort={handleContainerSort}
                    />
                    <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedContainers.map((container, index) => (
                    <TableRow key={container.id} className={index % 2 === 1 ? "bg-muted" : ""}>
                      <TableCell className="font-medium">{container.name}</TableCell>
                      <TableCell>{formatDate(container.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(container)}
                            title={t("containers.editContainer")}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteContainer(container)}
                            className="text-destructive hover:text-destructive"
                            title={t("containers.deleteContainer")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
